import { createContext, useContext, type ComponentType, type ReactNode } from "react";

// Everything app-specific is injected here. The package must not import an
// application's API client, auth store, i18n instance, or design system —
// that is what makes it reusable by the next product (extraction plan,
// Phase 5).

/** The slice of an HTTP client the pages need — nothing more. */
export interface LedgerApi {
	get<T>(path: string, params?: Record<string, unknown>): Promise<T>;
	post<T>(path: string, body?: unknown): Promise<T>;
	put<T>(path: string, body?: unknown): Promise<T>;
}

/**
 * Design-system primitives, injected rather than depended on: the host keeps
 * visual ownership, the package keeps zero styling opinions. Loosely typed on
 * purpose — the pages pass the usual props (children, onClick, variant, …)
 * and the host's components decide what they honour.
 */
export interface LedgerUIComponents {
	Card: ComponentType<any>;
	Spinner: ComponentType<any>;
	Button: ComponentType<any>;
	Input: ComponentType<any>;
	Modal: ComponentType<any>;
	Badge: ComponentType<any>;
}

export interface LedgerUIConfig {
	api: LedgerApi;
	/** Translation function; keys use the host's `accounting.*` namespace. */
	t: (key: string, options?: Record<string, unknown>) => string;
	/**
	 * Permission predicate. Role bypasses (owner/admin) are the HOST's rule
	 * and belong inside this function, not in the pages.
	 */
	can: (permission: string) => boolean;
	/** Money renderer; the ledger returns numbers, the host owns display. */
	formatMoney: (amount: number | null | undefined) => string;
	/** The active tenant (business) id, or null when none is selected. */
	tenantId: string | null;
	components: LedgerUIComponents;
}

const LedgerUIContext = createContext<LedgerUIConfig | null>(null);

export function LedgerUIProvider({ config, children }: { config: LedgerUIConfig; children: ReactNode }) {
	return <LedgerUIContext.Provider value={config}>{children}</LedgerUIContext.Provider>;
}

export function useLedgerUI(): LedgerUIConfig {
	const ctx = useContext(LedgerUIContext);
	if (!ctx) {
		throw new Error("useLedgerUI: no LedgerUIProvider above this component — wrap the accounting routes in one");
	}
	return ctx;
}
